/**
 * Utilities for persisting and managing the closest region info
 * Used to invalidate latency cache when user location changes significantly
 */

const CLOSEST_REGION_INFO_KEY = "portal-closest-region-info";
const DISTANCE_THRESHOLD_METERS = 100000; // 100km in meters

export interface ClosestRegionInfo {
  name: string;
  distanceM: number | null;
  timestamp: number;
}

/**
 * Persist the closest region info to localStorage
 */
export function persistClosestRegionInfo(
  regionInfo: Omit<ClosestRegionInfo, "timestamp">,
): void {
  try {
    const info: ClosestRegionInfo = {
      ...regionInfo,
      timestamp: Date.now(),
    };
    localStorage.setItem(CLOSEST_REGION_INFO_KEY, JSON.stringify(info));
  } catch {
    // ignore write errors
  }
}

/**
 * Load the closest region info from localStorage
 */
export function loadClosestRegionInfo(): ClosestRegionInfo | undefined {
  try {
    const serializedData = localStorage.getItem(CLOSEST_REGION_INFO_KEY);
    if (!serializedData) {
      return undefined;
    }
    return JSON.parse(serializedData) as ClosestRegionInfo;
  } catch {
    return undefined;
  }
}

/**
 * Check if the distance has changed significantly (more than 100km)
 * @param previousDistanceM Previous closest region distance in meters
 * @param currentDistanceM Current closest region distance in meters
 * @returns true if the distance changed by more than 100km
 */
export function hasDistanceChangedSignificantly(
  previousDistanceM: number | null,
  currentDistanceM: number | null,
): boolean {
  if (previousDistanceM === currentDistanceM) {
    return false;
  }

  if (previousDistanceM === null) {
    return currentDistanceM !== null;
  }

  if (currentDistanceM === null) {
    return previousDistanceM !== null;
  }

  const distanceChange = Math.abs(currentDistanceM - previousDistanceM);
  return distanceChange > DISTANCE_THRESHOLD_METERS;
}

/**
 * Clear the persisted closest region info
 */
export function clearClosestRegionInfo(): void {
  try {
    localStorage.removeItem(CLOSEST_REGION_INFO_KEY);
  } catch {
    // ignore errors
  }
}
